/**
 * 多租戶平台管理（批141，裁示 25／26／35／37／41）——對應後端 `app/api/admin_platforms.py`。
 *
 * 平台＝一把能以機器身分（IPOPLT1 每請求簽章）替旗下每個組織取證的鑰匙。登錄、換鑰、
 * 停權、解除組織關聯的影響面與產品憑證設計同級，所以**全部端點都是營運主管以上**
 * （連讀取也是；admin 403）。代理商不開放。
 *
 * 換鑰是兩段式：`setNextKey`（兩把都驗得過）→ 平台切換私鑰 → `promoteNextKey`
 * （舊私鑰立即失效）；設錯了用 `dropNextKey` 撤回。停權（`status=suspended`）下一個請求
 * 就生效：平台 realm 與帶平台標頭的心跳一律 401，續期暫停；復權後自動恢復。
 */
import { api, qs } from './client'

export type PlatformStatus = 'active' | 'suspended'

/** 三個逐平台限速覆寫（每 60 秒幾次；null＝沿用系統預設）。 */
export const LIMIT_FIELDS = [
  'realm_limit_per_min',
  'heartbeat_limit_per_min',
  'activate_limit_per_min',
] as const
export type LimitField = (typeof LIMIT_FIELDS)[number]

export interface PlatformStats {
  /** 在役組織數。 */
  tenants: number
  /** 已解除關聯的組織數。 */
  tenants_detached: number
  /** 在役組織中，現行證是正式證的數量。 */
  licensed: number
  /** 在役組織中，現行證是試用的數量。 */
  trials_active: number
  /** 領過試用的組織數（含已解除的；同一組織只算一次）。 */
  trials_issued: number
}

export interface Platform {
  id: string
  /** 平台代號（`^[a-z0-9][a-z0-9-]{1,31}$`，不可有點）；建立後不可改。 */
  platform_key: string
  name: string
  status: PlatformStatus | string
  /** 平台可以代辦的產品（`products.key`）。 */
  product_keys: string[]
  /** 現行公鑰（raw 32 bytes Ed25519 的標準 base64，44 字元）。 */
  public_key: string
  /** 公鑰指紋（sha256 前 16 碼）——畫面比對用。 */
  public_key_fp: string | null
  /** 換鑰中的新公鑰（兩把都驗得過）；null＝沒有換鑰中。 */
  public_key_next: string | null
  public_key_next_fp: string | null
  key_rotated_at: string | null
  realm_limit_per_min: number | null
  heartbeat_limit_per_min: number | null
  activate_limit_per_min: number | null
  /** 實際生效的上限（覆寫值，沒有就是系統預設）。 */
  effective_limits: Record<LimitField, number>
  last_seen_at: string | null
  notes: string | null
  /**
   * 批155：平台層的「自動核准」開關（雙層開關的第一層）。要與套裝層的 `auto_approve` **同時**
   * 打開，而且送件內容逐字等於套裝模板，平台送的申請才會當場核准；任一不成立照常轉人審。
   * 只有營運主管以上能改，每次切換都記稽核（`platform.auto_approve_set`）。舊後端缺鍵＝false。
   */
  auto_approve?: boolean
  created_by: string | null
  created_at: string | null
  updated_at: string | null
  stats: PlatformStats
}

export interface PlatformList {
  items: Platform[]
  /** 三個限速的系統預設值（覆寫欄留空時生效的值）。 */
  limit_defaults: Record<LimitField, number>
}

export interface PlatformIn {
  platform_key: string
  name: string
  product_keys: string[]
  public_key: string
  realm_limit_per_min?: number | null
  heartbeat_limit_per_min?: number | null
  activate_limit_per_min?: number | null
  notes?: string | null
}

/**
 * 部分更新（沒帶的欄位不動；限速三欄可明確送 null＝回到系統預設）。公鑰只走換鑰三支。
 * 批155：`auto_approve` 只有營運主管以上能改——**單獨送**（與其他欄位分開存），稽核才看得出是哪一個動作。
 */
export type PlatformPatch = Partial<
  Pick<PlatformIn, 'name' | 'product_keys' | LimitField | 'notes'> & {
    status: PlatformStatus
    auto_approve: boolean
  }
>

export interface TenantLicense {
  license_id: string
  status: string
  product: string
  plan: string | null
  trial: boolean
  expires_at: string | null
}

export interface PlatformTenant {
  id: string
  /** 平台那一側的組織識別（解除關聯時要逐字輸入它確認）。 */
  external_ref: string
  customer_id: string
  customer_name: string | null
  customer_kind: string | null
  contact_email: string | null
  instance_id: string
  created_at: string | null
  /** 非 null＝已解除關聯（不可逆；客戶與 instance 都不能再被任何平台認領）。 */
  detached_at: string | null
  /** 現行證摘要（在役組織才有；已解除的一律 null）。 */
  license: TenantLicense | null
  /** 現行證是不是試用。 */
  trial: boolean
  pending_request: { id: string; created_at: string | null } | null
}

export interface TenantPage {
  items: PlatformTenant[]
  /** 不透明游標；null＝沒有下一頁。 */
  next_cursor: string | null
}

export interface DetachResult {
  tenant: PlatformTenant
  revoked_licenses: number
  revoked_addons: number
  cancelled_requests: number
}

export const listPlatforms = () => api<PlatformList>('/api/admin/platforms')

export const getPlatform = (id: string) =>
  api<Platform & { limit_defaults: Record<LimitField, number> }>(`/api/admin/platforms/${id}`)

/** 登錄平台。409 `platform_key_exists`；欄位錯 422 `{code, field, message}`。 */
export const createPlatform = (body: PlatformIn) =>
  api<Platform>('/api/admin/platforms', { method: 'POST', body })

export const updatePlatform = (id: string, body: PlatformPatch) =>
  api<Platform>(`/api/admin/platforms/${id}`, { method: 'PUT', body })

/** 換鑰第一步：設新公鑰（之後兩把都驗得過）。 */
export const setNextKey = (id: string, publicKeyNext: string) =>
  api<Platform>(`/api/admin/platforms/${id}/keys`, {
    method: 'PUT',
    body: { public_key_next: publicKeyNext },
  })

/** 換鑰第三步：新公鑰生效、舊公鑰立即失效。沒有新公鑰 → 409 `no_next_key`。 */
export const promoteNextKey = (id: string) =>
  api<Platform>(`/api/admin/platforms/${id}/keys/promote`, { method: 'POST' })

/** 撤回尚未生效的新公鑰。 */
export const dropNextKey = (id: string) =>
  api<Platform>(`/api/admin/platforms/${id}/keys/next`, { method: 'DELETE' })

/** 平台旗下的組織（新到舊、keyset 分頁；`after`＝上一頁的 `next_cursor`）。 */
export const listPlatformTenants = (
  id: string,
  opts: { after?: string | null; limit?: number; includeDetached?: boolean } = {},
) =>
  api<TenantPage>(
    `/api/admin/platforms/${id}/tenants${qs({
      after: opts.after || undefined,
      limit: opts.limit ? String(opts.limit) : undefined,
      include_detached: opts.includeDetached ? 'true' : undefined,
    })}`,
  )

/**
 * 解除組織關聯（**不可逆**）：撤回待審申請、撤銷全部現行證、蓋解除時間。
 * `confirmExternalRef` 必須逐字等於該組織的 `external_ref`（否則 422 `confirm_mismatch`）。
 */
export const detachPlatformTenant = (id: string, tenantId: string, confirmExternalRef: string) =>
  api<DetachResult>(`/api/admin/platforms/${id}/tenants/${tenantId}/detach`, {
    method: 'POST',
    body: { confirm_external_ref: confirmExternalRef },
  })
